// Enhanced workspaceSlice.js
import { createSlice } from "@reduxjs/toolkit";

const workspaceSlice = createSlice({
  name: "workspace",
  initialState: {
    selectedIndicators: [],
    selectedMapFeatures: [],
    currentWorkspace: null,
    hasUnsavedChanges: false,
    originalWorkspaceData: null,
    isWorkspaceLoaded: false,
    loadedWorkspaceName: null,
    workspaceId: null,
    isNewWorkspaceStarted: false,
    savedCharts: [],
  },
  reducers: {
    addIndicator: (state, action) => {
      if (!state.selectedIndicators.some(ind => ind.indicator_code === action.payload.indicator_code)) {
        state.selectedIndicators.push(action.payload);
        // Reset new workspace flag when user starts adding indicators
        state.isNewWorkspaceStarted = false;
        // Only mark as changed if we have a loaded workspace
        if (state.isWorkspaceLoaded) {
          state.hasUnsavedChanges = true;
        }
      }
    },

    removeIndicator: (state, action) => {
      state.selectedIndicators = state.selectedIndicators.filter(
        ind => ind.indicator_code !== action.payload
      );
      // Only mark as changed if we have a loaded workspace
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    setIndicators: (state, action) => {
      state.selectedIndicators = action.payload;
      // Don't mark as changed if we're loading a workspace
      if (!state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = false;
      }
    },

    clearIndicators: (state) => {
      state.selectedIndicators = [];
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    addMapIndicator: (state, action) => {
      const { id, geo_level_code } = action.payload;
      if (!state.selectedMapFeatures.some(f => f.id === id && f.geo_level_code === geo_level_code)) {
        state.selectedMapFeatures.push(action.payload);
        // Reset new workspace flag when user starts adding locations
        state.isNewWorkspaceStarted = false;
        if (state.isWorkspaceLoaded) {
          state.hasUnsavedChanges = true;
        }
      }
    },

    removeMapIndicator: (state, action) => {
      const targetId = String(action.payload);
      const originalLength = state.selectedMapFeatures.length;

      state.selectedMapFeatures = state.selectedMapFeatures.filter(f => {
        const locationId = String(f.name || f.properties?.name || '');
        const shouldKeep = locationId !== targetId;
        return shouldKeep;
      });

      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    setMapIndicators: (state, action) => {
      state.selectedMapFeatures = action.payload;
      // Don't mark as changed if we're loading a workspace
      if (!state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = false;
      }
    },

    clearMapIndicators: (state) => {
      state.selectedMapFeatures = [];
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    // Enhanced workspace loading
    setWorkspaceLoaded: (state, action) => {
      state.isWorkspaceLoaded = true;
      state.loadedWorkspaceName = action.payload.name;
      state.workspaceId = action.payload.id;
      state.currentWorkspace = action.payload;
      state.hasUnsavedChanges = false;
      state.isNewWorkspaceStarted = false; // Reset flag when workspace is loaded
      console.log(state.savedCharts, "saved charts");

      // Store original data for comparison
      state.originalWorkspaceData = {
        indicators: JSON.parse(JSON.stringify(state.selectedIndicators)),
        mapFeatures: JSON.parse(JSON.stringify(state.selectedMapFeatures))
      };
    },

    // Mark workspace as modified
    markAsModified: (state) => {
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    // Clear workspace state - UPDATED for logout functionality
    clearWorkspace: (state) => {
      state.selectedIndicators = [];
      state.selectedMapFeatures = [];
      state.isWorkspaceLoaded = false;
      state.loadedWorkspaceName = null;
      state.workspaceId = null;
      state.currentWorkspace = null;
      state.hasUnsavedChanges = false;
      state.originalWorkspaceData = null;
      state.isNewWorkspaceStarted = false;  // ✅ RESET FLAG ON LOGOUT
      state.savedCharts = [];
    },

    // Clear persisted workspace data (for logout with saved workspace)
    clearPersistedWorkspace: (state) => {
      // This action will be handled by Redux Persist to clear localStorage
      return {
        selectedIndicators: [],
        selectedMapFeatures: [],
        isWorkspaceLoaded: false,
        loadedWorkspaceName: null,
        workspaceId: null,
        currentWorkspace: null,
        hasUnsavedChanges: false,
        originalWorkspaceData: null,
        isNewWorkspaceStarted: false,  // ✅ RESET FLAG ON LOGOUT
        savedCharts: [],
      };
    },

    // Updated after successful save/update
    markAsSaved: (state, action) => {
      state.hasUnsavedChanges = false;

      // Update workspace info regardless of new/update
      if (action.payload?.name) {
        state.loadedWorkspaceName = action.payload.name;
      }

      // If it's a new workspace, set the workspace as loaded
      if (action.payload?.isNew) {
        state.isWorkspaceLoaded = true;
        state.workspaceId = action.payload.id;
        state.currentWorkspace = action.payload.workspace;
      }

      // Update original data
      state.originalWorkspaceData = {
        indicators: JSON.parse(JSON.stringify(state.selectedIndicators)),
        mapFeatures: JSON.parse(JSON.stringify(state.selectedMapFeatures))
      };
    },

    // Load workspace data
    loadWorkspace: (state, action) => {
      const { workspace, indicators, mapFeatures } = action.payload;

      state.currentWorkspace = workspace;
      state.workspaceId = workspace.id;
      state.loadedWorkspaceName = workspace.ws_name || workspace.name;
      state.isWorkspaceLoaded = true;
      state.selectedIndicators = indicators || [];
      state.selectedMapFeatures = mapFeatures || [];
      state.hasUnsavedChanges = false;
      state.isNewWorkspaceStarted = false; // Reset flag when workspace is loaded
      state.savedCharts = [];

      // Store original data for comparison
      state.originalWorkspaceData = {
        indicators: JSON.parse(JSON.stringify(indicators || [])),
        mapFeatures: JSON.parse(JSON.stringify(mapFeatures || []))
      };
    },

    // Create new workspace while keeping current data
    createNewWorkspace: (state, action) => {
      // Keep current indicators and locations but clear workspace state
      state.isWorkspaceLoaded = false;
      state.loadedWorkspaceName = null;
      state.workspaceId = null;
      state.currentWorkspace = null;
      state.hasUnsavedChanges = false;
      state.originalWorkspaceData = null;
      state.savedCharts = [];
      // Don't clear selectedIndicators and selectedMapFeatures
    },

    // Start fresh workspace - clear everything and reset to "My Workspace" state
    startNewWorkspace: (state) => {
      state.selectedIndicators = [];
      state.selectedMapFeatures = [];
      state.isWorkspaceLoaded = false;
      state.loadedWorkspaceName = null;
      state.workspaceId = null;
      state.currentWorkspace = null;
      state.hasUnsavedChanges = false;
      state.originalWorkspaceData = null;
      state.isNewWorkspaceStarted = true;
      state.savedCharts = [];
    },

    // Reset the new workspace flag when user starts working
    resetNewWorkspaceFlag: (state) => {
      state.isNewWorkspaceStarted = false;
    },
    // ---------- NEW: Saved Charts ----------
    saveCharts: (state, action) => {
      // Add new chart to savedCharts
      state.savedCharts.push(action.payload);
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },

    removeSavedCharts: (state, action) => {
      // Remove chart by chartName
      state.savedCharts = state.savedCharts.filter(
        (chart) => chart.chartName !== action.payload
      );
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },
    clearSavedCharts: (state) => {
      state.savedCharts = [];
      if (state.isWorkspaceLoaded) {
        state.hasUnsavedChanges = true;
      }
    },
  }
});

export const {
  addIndicator,
  removeIndicator,
  clearIndicators,
  addMapIndicator,
  setIndicators,
  setMapIndicators,
  removeMapIndicator,
  clearMapIndicators,
  loadWorkspace,
  setWorkspaceLoaded,
  clearWorkspace,
  markAsModified,
  markAsSaved,
  createNewWorkspace,
  startNewWorkspace,
  resetNewWorkspaceFlag,
  clearPersistedWorkspace,
  saveCharts,
  clearSavedCharts,
  removeSavedCharts,
} = workspaceSlice.actions;

export default workspaceSlice.reducer;